import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  BadgeCheck,
  Loader2,
  RefreshCw,
  Search,
  UserPlus,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/layout/page-header';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { useAuthStore, useBusiness, usePermission } from '@/features/auth/auth.store';
import { cn } from '@/lib/utils';
import { StaffDetailSheet } from './staff-detail-sheet';
import {
  useAddToStore,
  useStaff,
  useTemporaryManagerCode,
  useUserSearch,
  type StaffProfile,
} from './staff.api';

/**
 * Who can work in this store, and what they are allowed to do.
 *
 * The original mixed two searches into one box: typing looked the person up
 * across the whole platform *and* filtered the list, so filtering the list
 * kept opening an "add somebody" modal. They are two different questions, so
 * they are two different controls here.
 */
export function StaffPage() {
  const { t } = useTranslation();
  const business = useBusiness();
  const staff = useStaff(business?.id ?? null);

  const isStoreAdmin = usePermission('isStoreAdmin');
  const isAdmin = usePermission('isAdmin');

  // The store may have granted the signed-in person the right to edit others
  // without giving them a platform role, which is the rule the original used.
  const canEditUsers = useAuthStore((state) =>
    (state.profile?.businessProfiles ?? []).some(
      (entry) =>
        entry.businessId === state.business?.id && entry.canEditUsers === true
    )
  );
  const canEdit = isAdmin || isStoreAdmin || canEditUsers;

  const [filter, setFilter] = useState('');
  const [hideInactive, setHideInactive] = useState(false);
  const [adding, setAdding] = useState(false);
  const [selected, setSelected] = useState<StaffProfile | null>(null);

  const tempCode = useTemporaryManagerCode();

  const term = filter.trim().toLowerCase();
  const rows = (staff.data ?? [])
    .filter((row) => (hideInactive ? row.isActive : true))
    .filter((row) =>
      term === ''
        ? true
        : `${row.completeName ?? ''} ${row.email ?? ''} ${row.phoneNumber ?? ''}`
            .toLowerCase()
            .includes(term)
    );

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('staff.title')}
        description={t('staff.subtitle')}
        actions={
          <>
            {isStoreAdmin && business && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={tempCode.isPending}
                  >
                    {tempCode.isPending ? (
                      <Loader2 className="animate-spin" />
                    ) : (
                      <BadgeCheck />
                    )}
                    <span className="hidden sm:inline">
                      {t('staff.provisionalCode')}
                    </span>
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      {t('staff.provisionalCode')}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      {t('staff.provisionalCodePrompt')}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() =>
                        tempCode.mutate(business.id, {
                          onSuccess: (envelope) => {
                            const code = envelope.message;
                            if (code) {
                              void navigator.clipboard.writeText(code);
                              toast.success(
                                t('staff.codeCopied', { code })
                              );
                            } else {
                              toast.error(t('staff.actionFailed'));
                            }
                          },
                          onError: () => toast.error(t('staff.actionFailed')),
                        })
                      }
                    >
                      {t('common.yes')}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            <Button
              variant="outline"
              size="sm"
              disabled={staff.isFetching}
              onClick={() => void staff.refetch()}
            >
              <RefreshCw className={staff.isFetching ? 'animate-spin' : ''} />
              <span className="hidden sm:inline">{t('staff.refresh')}</span>
            </Button>
            {canEdit && (
              <Button size="sm" onClick={() => setAdding(true)}>
                <UserPlus />
                <span className="hidden sm:inline">{t('staff.addStaff')}</span>
              </Button>
            )}
          </>
        }
      />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
          <Input
            value={filter}
            className="h-10 pl-9"
            placeholder={t('staff.filterPlaceholder')}
            onChange={(event) => setFilter(event.target.value)}
          />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <Switch checked={hideInactive} onCheckedChange={setHideInactive} />
          {t('staff.hideInactive')}
        </label>
      </div>

      {/* `isLoading`, not `isPending`: with no store selected the query never
          runs, and a pending skeleton would sit there forever. */}
      {staff.isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="h-16 rounded-xl" />
          ))}
        </div>
      )}

      {staff.isError && (
        <p className="text-muted-foreground rounded-xl border border-dashed py-12 text-center text-sm">
          {t('staff.loadFailed')}
        </p>
      )}

      {staff.isSuccess && rows.length === 0 && (
        <div className="relative rounded-xl">
          <BusyOverlay show={staff.isFetching} />
          <p className="text-muted-foreground rounded-xl border border-dashed py-12 text-center text-sm">
            {t('staff.noStaff')}
          </p>
        </div>
      )}

      {rows.length > 0 && (
        <div className="relative rounded-xl">
          <BusyOverlay show={staff.isFetching} />
          <div
            className={cn(
              'divide-y rounded-xl border',
              staff.isFetching && 'opacity-60 transition-opacity'
            )}
          >
            {rows.map((row) => (
              <button
                key={row.id}
                type="button"
                onClick={() => setSelected(row)}
                className="hover:bg-accent flex w-full items-center gap-3 p-3 text-left transition-colors"
              >
                <Avatar profile={row} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">
                    {row.completeName}
                  </p>
                  <p className="text-muted-foreground truncate text-xs">
                    {row.email}
                  </p>
                  {row.phoneNumber && (
                    <p className="text-muted-foreground truncate text-xs">
                      {row.phoneNumber}
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  <Badge variant={row.isActive ? 'success' : 'secondary'}>
                    {row.isActive ? t('staff.active') : t('staff.inactive')}
                  </Badge>
                  {row.isAdmin && (
                    <Badge variant="secondary">{t('staff.admin')}</Badge>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <AddStaffSheet
        businessId={business?.id ?? null}
        open={adding}
        onOpenChange={setAdding}
      />

      <StaffDetailSheet
        profile={selected}
        canEdit={canEdit}
        open={selected !== null}
        onOpenChange={(next) => {
          if (!next) setSelected(null);
        }}
      />
    </div>
  );
}

function Avatar({ profile }: { profile: StaffProfile }) {
  const initials = (profile.completeName ?? profile.email ?? '?')
    .split(' ')
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();

  if (profile.imageUrl) {
    return (
      <img
        src={profile.imageUrl}
        alt=""
        className="size-10 shrink-0 rounded-full border object-cover"
      />
    );
  }

  return (
    <span className="bg-muted text-muted-foreground flex size-10 shrink-0 items-center justify-center rounded-full border text-xs font-medium">
      {initials}
    </span>
  );
}

/**
 * Find an account on the platform and give it access to this store.
 *
 * Nothing is created: the person has to have signed up already, which is why
 * the empty state says so rather than offering a form.
 */
function AddStaffSheet({
  businessId,
  open,
  onOpenChange,
}: {
  businessId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const [term, setTerm] = useState('');
  const [query, setQuery] = useState('');
  const search = useUserSearch(open ? query : '');
  const add = useAddToStore();

  const rows = search.data ?? [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full gap-0 overflow-y-auto p-0 sm:max-w-lg"
      >
        <SheetHeader className="border-b pr-12">
          <SheetTitle>{t('staff.addStaff')}</SheetTitle>
          <p className="text-muted-foreground text-sm">
            {t('staff.addStaffHint')}
          </p>
        </SheetHeader>

        <div className="space-y-3 p-4">
          <form
            className="flex gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              setQuery(term.trim());
            }}
          >
            <Input
              value={term}
              placeholder={t('staff.searchPlaceholder')}
              onChange={(event) => setTerm(event.target.value)}
            />
            <Button type="submit" variant="outline" disabled={search.isFetching}>
              {search.isFetching ? (
                <Loader2 className="animate-spin" />
              ) : (
                <Search className="size-4" />
              )}
            </Button>
          </form>

          {query === '' && (
            <p className="text-muted-foreground rounded-xl border border-dashed py-10 text-center text-sm">
              {t('staff.searchToBegin')}
            </p>
          )}

          {search.isLoading && (
            <div className="divide-y rounded-xl border">
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="flex items-center gap-3 p-3">
                  <div className="flex-1 space-y-1.5">
                    <Skeleton className="h-4 w-40" />
                    <Skeleton className="h-3 w-56" />
                  </div>
                  <Skeleton className="h-8 w-16" />
                </div>
              ))}
            </div>
          )}

          {query !== '' && search.isSuccess && rows.length === 0 && (
            <p className="text-muted-foreground rounded-xl border border-dashed py-10 text-center text-sm">
              {t('staff.noUsers')}
            </p>
          )}

          {rows.length > 0 && (
            <div className="divide-y rounded-xl border">
              {rows.map((user) => (
                <div key={user.id} className="flex items-center gap-3 p-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">
                      {user.completeName}
                    </p>
                    <p className="text-muted-foreground truncate text-xs">
                      {user.email}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    disabled={!businessId || add.isPending}
                    onClick={() =>
                      add.mutate(
                        { userId: user.id, businessId: businessId! },
                        {
                          onSuccess: () => {
                            toast.success(t('staff.userAdded'));
                            onOpenChange(false);
                          },
                          onError: () => toast.error(t('staff.actionFailed')),
                        }
                      )
                    }
                  >
                    <UserPlus className="size-3.5" />
                    {t('staff.add')}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
